import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetListSortType, AssetListSortTypes } from '../../models';
import { AffectedAsset, MoveToAsset, SourceAsset } from './models/move-to-modal.model';

@Component({
  selector: 'mtm-move-to-modal',
  templateUrl: './move-to-modal.component.html',
  styleUrl: './move-to-modal.component.scss',
  standalone: false,
})
export class MoveToModalComponent implements OnInit {
  @Input() source: SourceAsset;
  @Input() assets: MoveToAsset[] = [];
  @Input() campaignId: string = '';

  displayAssets: MoveToAsset[] = [];
  currentSort: AssetListSortTypes = AssetListSortType.releaseDate;
  selectedProjectIds: string[] = [];
  selectedFocusValues: string[] = [];
  selectedTypeValues: string[] = [];
  selectedBetsValues: string[] = [];
  selectedDurationValues: string[] = [];
  selectedFormatValues: string[] = [];
  selectedCategoriesValues: string[] = [];
  selectedInUseValues: string[] = [];
  selectedStatusValues: string[] = [];
  showHiddenOnly: boolean = false;
  affectedAssets: Record<string, AffectedAsset> = {};
  selectedReleaseDateValues: string[] = [];
  selectedTargetAsset: MoveToAsset | null = null;

  constructor(private readonly activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    this.handleDisplayAssets();
  }

  public onCurrentSortChange(value: AssetListSortTypes): void {
    this.currentSort = value;
  }

  public onSelectedProjectIdsChange(value: string[]): void {
    this.selectedProjectIds = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedFocusValuesChange(value: string[]): void {
    this.selectedFocusValues = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedTypeValuesChange(value: string[]): void {
    this.selectedTypeValues = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedBetsValuesChange(value: string[]): void {
    this.selectedBetsValues = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedDurationValuesChange(value: string[]): void {
    this.selectedDurationValues = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedFormatValuesChange(value: string[]): void {
    this.selectedFormatValues = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedCategoriesValuesChange(value: string[]): void {
    this.selectedCategoriesValues = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedInUseValuesChange(value: string[]): void {
    this.selectedInUseValues = [...value];
    this.handleDisplayAssets();
  }

  public onSelectedStatusValuesChange(value: string[]): void {
    this.selectedStatusValues = [...value];
    this.handleDisplayAssets();
  }

  public onShowHiddenOnlyChange(value: boolean): void {
    this.showHiddenOnly = value;
    this.handleDisplayAssets();
  }

  public onSelectedReleaseDateValuesChange(value: string[]): void {
    this.selectedReleaseDateValues = [...value];
    this.handleDisplayAssets();
  }

  public handleDisplayAssets(): void {
    if (!this.assets?.length) {
      this.displayAssets = [];
      return;
    }

    const result: MoveToAsset[] = this.assets.filter((asset: MoveToAsset) => this.matchesAllFilters(asset));
    this.displayAssets = [...result];
  }

  private matchesAllFilters(asset: MoveToAsset): boolean {
    return (
      this.matchesProjectFilter(asset) &&
      this.matchesFocusFilter(asset) &&
      this.matchesTypeFilter(asset) &&
      this.matchesBetsFilter(asset) &&
      this.matchesDurationFilter(asset) &&
      this.matchesFormatFilter(asset) &&
      this.matchesCategoriesFilter(asset) &&
      this.matchesHiddenFilter(asset) &&
      this.matchesInUseFilter(asset) &&
      this.matchesStatusFilter(asset) &&
      this.matchesReleaseDateFilter(asset)
    );
  }

  private matchesProjectFilter(asset: MoveToAsset): boolean {
    if (!this.selectedProjectIds?.length) {
      return true;
    }
    return this.selectedProjectIds.includes(asset?.projectId);
  }

  private matchesFocusFilter(asset: MoveToAsset): boolean {
    if (!this.selectedFocusValues?.length) {
      return true;
    }
    return asset?.focus ? this.selectedFocusValues.includes(asset.focus) : false;
  }

  private matchesTypeFilter(asset: MoveToAsset): boolean {
    if (!this.selectedTypeValues?.length) {
      return true;
    }
    return asset?.type ? this.selectedTypeValues.includes(asset.type) : false;
  }

  private matchesBetsFilter(asset: MoveToAsset): boolean {
    if (!this.selectedBetsValues?.length) {
      return true;
    }
    if (!asset?.bets || !Array.isArray(asset.bets) || asset.bets.length === 0) {
      return false;
    }
    return asset.bets.some((bet: string) => this.selectedBetsValues.includes(bet));
  }

  private matchesDurationFilter(asset: MoveToAsset): boolean {
    if (!this.selectedDurationValues?.length) {
      return true;
    }
    if (!asset?.length || !Array.isArray(asset.length) || asset.length.length === 0) {
      return false;
    }
    return asset.length.some((len: string) =>
      this.selectedDurationValues.some((duration: string) => len.toUpperCase().startsWith(duration.toUpperCase()))
    );
  }

  private matchesFormatFilter(asset: MoveToAsset): boolean {
    if (!this.selectedFormatValues?.length) {
      return true;
    }
    if (!asset?.format || !Array.isArray(asset.format) || asset.format.length === 0) {
      return false;
    }
    return asset.format.some((fmt: string) => this.selectedFormatValues.includes(fmt));
  }

  private matchesCategoriesFilter(asset: MoveToAsset): boolean {
    if (!this.selectedCategoriesValues?.length) {
      return true;
    }
    if (!asset?.categories || !Array.isArray(asset.categories) || asset.categories.length === 0) {
      return false;
    }
    return asset.categories.some((cat: string) => this.selectedCategoriesValues.includes(cat));
  }

  private matchesHiddenFilter(asset: MoveToAsset): boolean {
    if (!this.showHiddenOnly) {
      return true;
    }
    return asset.hidden === true;
  }

  private matchesInUseFilter(asset: MoveToAsset): boolean {
    if (!this.selectedInUseValues?.length) {
      return true;
    }
    const isInUse: boolean = !!this.affectedAssets[asset.id];
    const hasYes: boolean = this.selectedInUseValues.includes('YES');
    const hasNo: boolean = this.selectedInUseValues.includes('NO');

    if (hasYes && hasNo) {
      return true;
    }
    if (hasYes) {
      return isInUse;
    }
    if (hasNo) {
      return !isInUse;
    }
    return true;
  }

  private matchesStatusFilter(asset: MoveToAsset): boolean {
    if (!this.selectedStatusValues?.length) {
      return true;
    }
    const affectedAsset: AffectedAsset | undefined = this.affectedAssets[asset.id];
    const assetStatus: string = affectedAsset ? affectedAsset.status || 'UNKNOWN' : 'NOT_AFFECTED';
    return this.selectedStatusValues.includes(assetStatus);
  }

  private matchesReleaseDateFilter(asset: MoveToAsset): boolean {
    if (!this.selectedReleaseDateValues?.length) {
      return true;
    }
    if (!asset?.releaseDate) {
      return false;
    }
    try {
      let assetReleaseDate: Date;
      if (typeof asset.releaseDate === 'string') {
        assetReleaseDate = new Date(Number.parseInt(asset.releaseDate, 10));
      } else {
        // Handle object format: { year: number; month: number; day: number }
        const dateObj: { year: number; month: number; day: number } = asset.releaseDate as {
          year: number;
          month: number;
          day: number;
        };
        assetReleaseDate = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
      }
      const assetYear: string = assetReleaseDate.getFullYear().toString();
      const assetMonth: string = String(assetReleaseDate.getMonth() + 1).padStart(2, '0');
      const assetYearMonth: string = `${assetYear}-${assetMonth}`;
      return this.selectedReleaseDateValues.includes(assetYearMonth);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error parsing release date:', asset.releaseDate, error);
      return false;
    }
  }

  public onAssetSelect(target: MoveToAsset): void {
    this.selectedTargetAsset = target;
  }

  public onCancel(): void {
    this.activeModal.close(null);
  }

  public onSave(): void {
    this.activeModal.close(this.selectedTargetAsset);
  }
}
